/**
 * Server timestamps → the viewer's local timezone.
 *
 * The MySQL connection runs in UTC, so any bare datetime string the API returns
 * ("2025-10-13 06:35:38") is a UTC instant with no marker saying so. Passing
 * that straight to `new Date()` / `Date.parse()` is the classic bug: engines
 * treat a space- or T-separated string *without* a zone as LOCAL time, so a
 * viewer on GMT+2 sees 06:35 when the event really happened at 08:35 their time.
 *
 * Everything here funnels through `parseServerDate`, which tags bare strings as
 * UTC before parsing. Millisecond epochs are already absolute instants and are
 * passed through untouched.
 */

// Below this a numeric timestamp is far more likely to be seconds than ms
// (1e11 ms is 1973; epoch seconds today are ~1.7e9).
const SECONDS_EPOCH_CEILING = 1e11

// Has an explicit zone: trailing Z, or a ±hh:mm / ±hhmm offset.
const HAS_TIMEZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i

// "YYYY-MM-DD HH:MM:SS", "YYYY-MM-DDTHH:MM:SS", optional fractional seconds.
const BARE_DATETIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/

// "YYYY-MM-DD" with no time part at all.
const BARE_DATE = /^\d{4}-\d{2}-\d{2}$/

/**
 * Parse a server-supplied timestamp into a Date positioned at the correct
 * absolute instant. Returns `null` when the value can't be interpreted, so
 * callers can fall back to showing the raw value instead of "Invalid Date".
 */
export const parseServerDate = (value) => {
  if (value === null || value === undefined || value === '') return null

  // Numeric epoch (or a numeric string) — already absolute, no zone ambiguity.
  if (typeof value === 'number' || (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value)))) {
    const n = Number(value)
    if (!Number.isFinite(n)) return null
    const ms = Math.abs(n) < SECONDS_EPOCH_CEILING ? n * 1000 : n
    const d = new Date(ms)
    return Number.isNaN(d.getTime()) ? null : d
  }

  if (typeof value !== 'string') return null
  const raw = value.trim()

  // Already carries a zone — trust it.
  if (HAS_TIMEZONE.test(raw)) {
    const d = new Date(raw)
    return Number.isNaN(d.getTime()) ? null : d
  }

  // Bare datetime from MySQL: it is UTC, so say so explicitly.
  if (BARE_DATETIME.test(raw)) {
    const d = new Date(`${raw.replace(' ', 'T')}Z`)
    return Number.isNaN(d.getTime()) ? null : d
  }

  // Date only — midnight UTC.
  if (BARE_DATE.test(raw)) {
    const d = new Date(`${raw}T00:00:00Z`)
    return Number.isNaN(d.getTime()) ? null : d
  }

  const fallback = new Date(raw)
  return Number.isNaN(fallback.getTime()) ? null : fallback
}

/** Epoch milliseconds for a server timestamp, or `null`. */
export const toEpochMs = (value) => {
  const d = parseServerDate(value)
  return d ? d.getTime() : null
}

const DEFAULT_DATETIME_OPTS = {
  year: 'numeric', month: 'short', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit'
}

/** Full local date + time. Falls back to the raw value when unparseable. */
export const formatLocalDateTime = (value, options = DEFAULT_DATETIME_OPTS) => {
  const d = parseServerDate(value)
  if (!d) return value === null || value === undefined ? '—' : String(value)
  return d.toLocaleString(undefined, options)
}

/** Local date only — for list columns where the time is noise. */
export const formatLocalDate = (value) => {
  const d = parseServerDate(value)
  if (!d) return value === null || value === undefined ? '—' : String(value)
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

/*
 * "about 1 month ago".
 *
 * Written here rather than pulled in: neither date-fns nor dayjs is a
 * dependency of this app, and a whole date library for one sentence is not a
 * trade worth making. The wording follows date-fns's `formatDistanceToNow` with
 * `addSuffix`, because that is the phrasing the design was drawn against —
 * including the "about" hedge, which is doing real work: an alert written five
 * weeks ago is not "1 month ago", and saying so precisely would be a lie the
 * reader can catch.
 *
 * Everything goes through `parseServerDate`, so a bare MySQL datetime is read as
 * UTC rather than as the viewer's local wall clock. Getting that wrong is how a
 * notice written ten minutes ago renders as "about 2 hours ago" for anyone east
 * of London.
 */
const MINUTE = 60
const HOUR = MINUTE * 60
const DAY = HOUR * 24
const MONTH = DAY * 30
const YEAR = DAY * 365

const plural = (value, unit) => `${value} ${unit}${value === 1 ? '' : 's'}`

/**
 * A coarse, human "how long ago" for a server timestamp.
 *
 * Returns '' when the value cannot be parsed, so callers can drop the line
 * entirely instead of rendering "Invalid Date ago".
 *
 * Future timestamps read "just now" rather than "in 3 minutes": the only way to
 * get one is a clock skew of a few seconds between the server and the viewer,
 * and a notice claiming to arrive from the future is more alarming than a
 * slightly early "just now".
 */
export const formatRelativeTime = (value, now = Date.now()) => {
  const date = parseServerDate(value)
  if (!date) return ''

  const seconds = Math.round((now - date.getTime()) / 1000)
  if (seconds < 45) return 'just now'
  if (seconds < 90) return 'about 1 minute ago'
  if (seconds < HOUR) return `${plural(Math.round(seconds / MINUTE), 'minute')} ago`
  if (seconds < HOUR * 2) return 'about 1 hour ago'
  if (seconds < DAY) return `about ${plural(Math.round(seconds / HOUR), 'hour')} ago`
  if (seconds < DAY * 2) return 'yesterday'
  if (seconds < MONTH) return `${plural(Math.round(seconds / DAY), 'day')} ago`
  if (seconds < MONTH * 2) return 'about 1 month ago'
  if (seconds < YEAR) return `${plural(Math.round(seconds / MONTH), 'month')} ago`
  if (seconds < YEAR * 2) return 'about 1 year ago'
  return `${plural(Math.round(seconds / YEAR), 'year')} ago`
}

/** The viewer's zone, e.g. "GMT+2" — used to label times as local. */
export const localTimeZoneLabel = () => {
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'shortOffset' })
      .formatToParts(new Date())
    return parts.find(p => p.type === 'timeZoneName')?.value
      ?? Intl.DateTimeFormat().resolvedOptions().timeZone
      ?? 'local time'
  } catch {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'local time'
  }
}
